import _ from "lodash";
import { ComponentUtils, DomUtils, MoModelConfig, MoModel, MoUtils, QueryStoreConfig, QueryType, QueryStore } from "ui-common";
import { EmbedsFusionSearchStore } from "../stores/EmbedsFusionSearchStore";
import { beaconAgent } from "./beacon/beacon-agent";
import { getConfig, parseConfig } from "./embeddable-helper";
import { EmbeddableAgentData, EmbeddableGlobalConfig, PublicSearchStore } from "./interfaces";
import { configureStoreApiService, createQueryStore } from "./embeds-store-api-helper";
import { AppStudioInitiator } from "../app-studio/services/app-studio-initiator";

class EmbeddableAgent extends MoModel {
  public defaultGroupName: string = 'main';
  public urlQueryParam: string;
  public currentQueryId: string;
  public storeApiService;
  public initialize = _.noop;

  protected data: EmbeddableAgentData = { stores: {} };

  private scriptTagConfigKeys = [
    'api-url', 'embed-token', 'app-studio-api-url', 'app-studio-embed-token', 'signal-store-api-url', 'signal-store-embed-token',
    'deep-linking', 'query-profile-id', 'api-type', 'fusion-api-url', 'fusion-doc-field-map', 'beacon', 'initiator', 'beacon-priority',
  ];

  private publicSearchStore: PublicSearchStore;
  private globalConfig: EmbeddableGlobalConfig;
  private isSpringboard: boolean = true;
  private configReady: boolean = false;
  private initiator;
  private logVerboseInfo: boolean = localStorage.enableEmbedDev === 'verbose';

  constructor(data: EmbeddableAgentData = { stores: {} }, config?: MoModelConfig) {
    super(data, config);

    this.initialize = _.partial(this.doInitialize, config);
    this.initialize();
  }

  doInitialize(config?) {
    this.setupConfig();

    if (this.configReady) {
      Object.defineProperty(window, 'getSearchStore', {
        value: () => this.isSpringboard ? this.getPublicSearchStore() : _.noop,
        writable: false,
        configurable: true
      });

      // REVIEW: The indicator of `initiator` could also be configured from embeds config.
      if (this.globalConfig?.initiator === 'AS') {
        this.setApiService();
        this.initiator = new AppStudioInitiator(this.data, config);

        console.log('Configuring Embeds with AS Initiator...', this.initiator);
      }

      this.onReady();
      MoUtils.waitFor(500).then(() => this.setupBeacon());
    }
  }

  setupConfig(force: boolean = !this.globalConfig) {
    const libElement = this.getLibScriptElement();
    let config;

    if (force && libElement) {
      // Get config attributes listed in `scriptTagConfigKeys`.
      // It also supports configuration through dataset attributes.
      // This allows both `<script api-uri="..." beacon="">` and `<script data-api-uri="..." data-beacon="">` to work.

      const getConfigAttr = (key: string) => libElement.getAttribute(key) || libElement.dataset[_.camelCase(key)];

      config = _.transform(this.scriptTagConfigKeys, (result, key) => result[_.camelCase(key)] = getConfigAttr(key), {} as any);
      config.deepLinking = _.isString(config.deepLinking) ? (config.deepLinking === 'true') : config.deepLinking;
      config.beacon = parseConfig(config.beacon);
      config.apiType ||= 'springboard';
      config.apiUrl ||= config.appStudioApiUrl;
      config.embedToken ||= config.appStudioEmbedToken;

      this.globalConfig = config;
      this.isSpringboard = this.globalConfig.apiType === 'springboard';
      this.configReady = true;
    }
  }

  setupBeacon() {
    const customBeaconConfig = this.globalConfig?.beacon;

    if (customBeaconConfig) {
      const config = this.globalConfig;

      const apiConfig = {
        apiUrl: config.signalStoreApiUrl || config.apiUrl ,
        embedToken: config.signalStoreEmbedToken || config.embedToken,
      };

      const beaconConfig = _.defaultsDeep(config.beacon, { api: apiConfig });

      beaconAgent.setup(beaconConfig, {
        getEmbedQueryId: () => this.currentQueryId,
        preferAttributeConfig: config.beaconPriority === 'attribute',
      });
    }
  }

  onReady(callback: (store: PublicSearchStore) => void = window.onSearchStoreReady) {
    if (_.isFunction(callback) && this.isSpringboard) {
      callback(this.getPublicSearchStore());
    }
  }

  getLibScriptElement(): HTMLElement {
    // Check for injected beacon first (dev tool priority)
    const injectedElement = document.querySelector('#lw-ui-lib[beacon][mode=injected]') as HTMLElement;
    if (injectedElement) {
      return injectedElement;
    }

    // Fall back to production embeddable script tag
    const element = document.getElementById('lw-ui-lib');

    if (this.logVerboseInfo && !element) {
      console.error(
        'Failed to load springboard UI library. Please make sure the script tag is included with id "lw-ui-lib"\n\n',
        'For example:\n\n <script id="lw-ui-lib" type="module" src=".../springboard.esm.js" api-url="https://<appId>.applications.lucidworks.com" embed-token="ZD3Snc9whoTsKHcSGsEWmopsaxWk993u"></script>'
      );
    }

    return element;
  }

  getPublicSearchStore() {
    if (!this.publicSearchStore) {
      /** Created an external store instance which only exposes the general search APIs. */
      const exposedApis: { [key in QueryType]: Array<keyof PublicSearchStore> } = {
        search: ['search', 'browse', 'metatags', 'discoverFields', 'subscribe', 'setSearch', 'setQuery', 'setFacets', 'setPage', 'setSort'],
        searchahead: ['searchahead'],
        detail: ['detail'],
        relatedContent: ['relatedContent'],
        signals: ['clickSignal', 'querySignal']
      };

      this.publicSearchStore = _.transform(exposedApis, (results: any, apis: Array<keyof PublicSearchStore>, type: string) => {
        const customConfig = { storeDataInZones: false, lazyLoadOnSubscribe: true };
        const store = _.bindAll(this.createNewStore({ type: type as QueryType, ...customConfig }, true), apis);

        _.each(apis, (api) => results[api] = store[api]);
      });

      Object.freeze(this.publicSearchStore);
    }

    return this.publicSearchStore;
  }

  setApiService() {
    this.setupConfig();

    this.data = {
      ...this.data,
      config: this.globalConfig,
      apiService: configureStoreApiService(this.globalConfig),
    };

    this.storeApiService = this.data.apiService;
    this.notifyUpdates({ action: 'api-service-ready' });
  }

  getStore(context, config: QueryStoreConfig, subscribeCallback?): any {
    const group = context.group ?? this.defaultGroupName;
    const stores = this.data.stores[group] ?? (this.data.stores[group] = {});
    let store: any = stores[config.type];
    let readyForSubscribe = Promise.resolve();

    if (store) {
      readyForSubscribe = store.setQueryParams(config.queryParams);
    } else {
      store = this.createNewStore(config);
      stores[config.type] = store;
    }

    readyForSubscribe.then(() => {
      if (_.isFunction(subscribeCallback)) {
        store.subscribe(subscribeCallback, context);
      }
    });

    return store;
  }

  setQuery(query: string, context) {
    this.data.query = query;
    this.notifyUpdates({ action: 'query' });
    this.getStore(context, { type: 'search' }).setQuery(query);
  }

  /**
   * Apply the component's deepLinking query params config to the deepLinking map.
   * 
   * Steps for setting up deep-linking in embeddable component:
   * 
   * 1. Defined the private property which the query param will map to. For example,
   *    persistQuery: string;
   * 
   * 2. Defined @Prop() deepLinkingStates with [private property] => [query param] maps. For example,
   *    @Prop() deepLinkingStates { persistQuery: 'query' };
   * 
   * 3. In component.componentWillLoad(), call configureDeepLinking(this);
   * 
   * 👉 Then the url param `?query=foo` will map to LwSearchAheadInstance.persistQuery by default.
   * 
   * 4. The query param can be configured via component config. For example,
   *    <lw-searchahead config="{ deepLinking: { query: 'q' }" } />
   * 
   * 👉 With above config, it will map the custom query param `?q=foo` to LwSearchAhead.persistQuery.
   * 
   * @param context Embeddable components.
   */
  configureDeepLinking(context) {
    this.setupConfig();

    if (this.globalConfig?.deepLinking && !DomUtils.isLocationInteractionBlocked()) {
      const { deepLinkingStates, config } = context;
      const parsedConfig = getConfig(config) || {};

      if (parsedConfig.deepLinking !== false) {
        _.each(_.keys(deepLinkingStates), (key) => this.mapDeepLinkingParam(deepLinkingStates, parsedConfig.deepLinking, key));
        ComponentUtils.setupAddOns(context, { deepLinking: true });
      }
    }
  }

  private mapDeepLinkingParam(deepLinkingStates, deepLinking = {}, key: string = '') {
    deepLinkingStates[key] = deepLinking[deepLinkingStates[key]] || deepLinkingStates[key];
  }

  private createNewStore(config: QueryStoreConfig, isPublicStore = false) {
    let store: QueryStore | EmbedsFusionSearchStore;

    if (!this.data?.apiService) {
      this.setApiService();
    }

    store = createQueryStore(config, this.globalConfig, isPublicStore);

    if (this.isSpringboard && config.type === 'search') {
      store.subscribe(() => this.currentQueryId = store.meta.currentParams.queryId);
    }

    return store;
  }
}

export const embeddableAgent = new EmbeddableAgent();